import { type FC, useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, Link, useLocation } from 'react-router-dom';
import { supabase } from '@/lib/api/supabase';
import type { SpecialEvent, DbSpecialEvent, Addon, DbAddon } from '@/types';
import { dbToSpecialEvent, dbToAddon } from '@/types';
import { AlertCircle, ListOrdered, LogIn } from 'lucide-react';
import { Button } from '@/components/ui';
import { useAddonWaitlist } from '@/hooks/data/useAddonWaitlist';
import { useAuth } from '@/hooks';
import { REGISTRATIONS_CLOSED } from '@/lib/registrationsClosed';
import { ClosedNotice } from '@/components/shared';
import {
  HeroSection,
  EventInfoSection,
  LocationSection,
  ItinerarySection,
  DressCodeSection,
  GallerySection,
} from '@/components/special-event';

/**
 * Special event detail page
 * Displays a specific special event by slug
 */
export const SpecialEventDetailPage: FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const isLoggedIn = !!user;
  const [specialEvent, setSpecialEvent] = useState<SpecialEvent | null>(null);
  const [linkedAddon, setLinkedAddon] = useState<Addon | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isOnWaitlist, setIsOnWaitlist] = useState(false);
  const [_waitlistPosition, setWaitlistPosition] = useState<number | null>(null);
  const [_waitlistError, setWaitlistError] = useState<string | null>(null);
  const actionInFlight = useRef(false);

  const {
    joinWaitlist: joinAddonWaitlist,
    leaveWaitlist: leaveAddonWaitlist,
    getUserWaitlistEntry: getAddonWaitlistEntry,
    loading: waitlistLoading,
  } = useAddonWaitlist();

  useEffect(() => {
    let cancelled = false;

    const fetchEvent = async (): Promise<void> => {
      if (!slug) {
        if (!cancelled) {
          setError('Event not found');
          setIsLoading(false);
        }
        return;
      }

      try {
        if (!cancelled) setIsLoading(true);
        if (!cancelled) setError(null);

        const { data, error: fetchError } = await supabase
          .from('special_events')
          .select('*, linked_addon:addons!linked_addon_id(*)')
          .eq('slug', slug)
          .eq('status', 'published')
          .eq('visible', true)
          .single();

        if (cancelled) return;

        if (fetchError) {
          if (fetchError.code === 'PGRST116') {
            setError('Event not found');
          } else {
            throw fetchError;
          }
          return;
        }

        if (data) {
          setSpecialEvent(dbToSpecialEvent(data as DbSpecialEvent));

          if ((data as any).linked_addon) {
            setLinkedAddon(dbToAddon((data as any).linked_addon as DbAddon));
          }
        }
      } catch (err) {
        if (cancelled) return;
        console.error('Failed to fetch special event:', err);
        setError(err instanceof Error ? err.message : 'Failed to load event');
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    void fetchEvent();
    return () => { cancelled = true; };
  }, [slug]);

  // Check waitlist status using linked addon
  useEffect(() => {
    if (!isLoggedIn || !specialEvent?.linkedAddonId) {
      setIsOnWaitlist(false);
      setWaitlistPosition(null);
      return;
    }

    let cancelled = false;

    const checkWaitlist = async () => {
      const entry = await getAddonWaitlistEntry(specialEvent.linkedAddonId!);
      if (cancelled) return;
      if (entry) {
        setIsOnWaitlist(true);
        setWaitlistPosition(entry.position);
      } else {
        setIsOnWaitlist(false);
        setWaitlistPosition(null);
      }
    };

    void checkWaitlist();
    return () => { cancelled = true; };
  }, [isLoggedIn, specialEvent?.linkedAddonId, getAddonWaitlistEntry]);

  const handleJoinWaitlist = useCallback(async () => {
    if (!specialEvent?.linkedAddonId || actionInFlight.current) return;
    actionInFlight.current = true;
    setWaitlistError(null);
    try {
      const result = await joinAddonWaitlist(specialEvent.linkedAddonId);
      if (result.success) {
        setIsOnWaitlist(true);
        setWaitlistPosition(result.position ?? null);
      } else {
        setWaitlistError(result.error ?? 'Failed');
      }
    } finally {
      actionInFlight.current = false;
    }
  }, [specialEvent?.linkedAddonId, joinAddonWaitlist]);

  const handleLeaveWaitlist = useCallback(async () => {
    if (!specialEvent?.linkedAddonId || actionInFlight.current) return;
    actionInFlight.current = true;
    setWaitlistError(null);
    try {
      const result = await leaveAddonWaitlist(specialEvent.linkedAddonId);
      if (result.success) {
        setIsOnWaitlist(false);
        setWaitlistPosition(null);
      } else {
        setWaitlistError(result.error ?? 'Failed');
      }
    } finally {
      actionInFlight.current = false;
    }
  }, [specialEvent?.linkedAddonId, leaveAddonWaitlist]);

  if (isLoading) {
    return (
      <div className="bg-gradient-to-b from-turquoise-800 via-turquoise-700 to-turquoise-800 min-h-screen">
        <div className="container mx-auto px-4 py-12">
          <div className="text-center">
            <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-orange-500 border-r-transparent"></div>
            <p className="mt-4 text-white">Loading event...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error || !specialEvent) {
    return (
      <div className="bg-gradient-to-b from-turquoise-800 via-turquoise-700 to-turquoise-800 min-h-screen">
        <div className="container mx-auto px-4 py-12">
          <div className="text-center py-20">
            <h1 className="text-4xl font-viking text-white mb-4">Event Not Found</h1>
            <p className="text-lg text-orange-200 mb-8">
              {error || 'The event you are looking for does not exist or is no longer available.'}
            </p>
            <button
              onClick={() => navigate('/special-events')}
              className="bg-orange-500 text-white hover:bg-orange-600 px-6 py-3 rounded-md font-medium transition-colors"
            >
              View All Events
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Compute inventory status from linked add-on
  const isSoldOut: boolean = !!linkedAddon && linkedAddon.hasInventory && linkedAddon.stockQuantity !== null && linkedAddon.stockQuantity <= 0;

  const spotsRemaining = linkedAddon?.hasInventory
    ? linkedAddon.stockQuantity
    : null;

  return (
    <div className="bg-white">
      <HeroSection event={specialEvent} isSoldOut={isSoldOut} />

      {/* Inventory status banner from linked add-on (only when addon tracks inventory) */}
      {linkedAddon && linkedAddon.hasInventory && (
        <div className={`text-center py-4 px-6 ${isSoldOut ? 'bg-red-600' : 'bg-turquoise-700'}`}>
          {isSoldOut ? (
            <div className="space-y-3">
              <div className="flex items-center justify-center gap-2 text-white">
                <AlertCircle className="h-5 w-5" />
                <p className="text-lg font-viking">Sold Out</p>
              </div>
              {/* Waitlist UI */}
              {isOnWaitlist ? (
                <div className="flex flex-col items-center gap-2">
                  <p className="text-white text-sm">
                    <ListOrdered className="h-4 w-4 inline mr-1" />
                    You're on the waitlist
                  </p>
                  <Button
                    size="sm"
                    variant="outline"
                    className="text-white border-white/50 hover:bg-white/20"
                    onClick={handleLeaveWaitlist}
                    disabled={waitlistLoading}
                  >
                    Leave Waitlist
                  </Button>
                </div>
              ) : REGISTRATIONS_CLOSED ? (
                <ClosedNotice className="text-white/80" />
              ) : isLoggedIn ? (
                <Button
                  size="sm"
                  className="bg-white text-red-600 hover:bg-white/90"
                  onClick={handleJoinWaitlist}
                  disabled={waitlistLoading}
                >
                  <ListOrdered className="h-4 w-4 mr-1" />
                  Join Waitlist
                </Button>
              ) : (
                <p className="text-white/80 text-sm">
                  <LogIn className="h-4 w-4 inline mr-1" />
                  <Link to="/login" state={{ returnTo: location.pathname }} className="underline hover:text-white">Log in</Link> to join the waitlist
                </p>
              )}
            </div>
          ) : spotsRemaining !== null && spotsRemaining <= 10 ? (
            <p className="text-white font-medium">
              Only {spotsRemaining} ticket{spotsRemaining !== 1 ? 's' : ''} remaining!
            </p>
          ) : spotsRemaining !== null ? (
            <p className="text-white/90 text-sm">
              {spotsRemaining} ticket{spotsRemaining !== 1 ? 's' : ''} available
            </p>
          ) : null}
        </div>
      )}

      <EventInfoSection event={specialEvent} />
      <ItinerarySection event={specialEvent} />
      <DressCodeSection event={specialEvent} />
      <LocationSection event={specialEvent} />
      <GallerySection event={specialEvent} />
    </div>
  );
};
